/**
 * InvoicingDashboard — Phase 1 of the Squarespace-level Invoicing Suite.
 *
 * Sections (top → bottom):
 *   • Top header with "Invoicing" title + Create-{Invoice,Estimate,Proposal,Contract} buttons + Settings cog
 *   • Welcome banner (collapsible promo card)
 *   • Revenue analytics — three cards (Revenue, Paid, Outstanding)
 *   • Setup checklist with completion %
 *   • Activity feed tabs (Invoices · Documents)
 *   • Right rail with rotating Business Tips + Payment Methods panel
 *
 * Data is loaded in one call: GET /api/builder/invoicing/dashboard.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Receipt, FileText, FileSignature, FilePen, Settings as SettingsIcon,
  Sparkles, TrendingUp, CheckCircle2, Circle, ArrowRight, Plus,
  CreditCard, ChevronRight, ExternalLink, AlertCircle, Lightbulb, Users,
} from 'lucide-react';
import { GlassCard } from '@/components/dashboard/dashboard-ui';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const STATUS_COLORS = {
  draft:    'bg-white/10 text-white/70',
  sent:     'bg-cyan-500/15 text-cyan-200 border border-cyan-500/30',
  open:     'bg-cyan-500/15 text-cyan-200 border border-cyan-500/30',
  viewed:   'bg-violet-500/15 text-violet-200 border border-violet-500/30',
  paid:     'bg-emerald-500/15 text-emerald-200 border border-emerald-500/30',
  overdue:  'bg-rose-500/15 text-rose-200 border border-rose-500/30',
  voided:   'bg-white/5 text-white/40 line-through',
};

const fmtMoney = (cents) => {
  const v = (Number(cents) || 0) / 100;
  return v.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return '—'; }
};

/* ───────── Top header ───────── */
function TopHeader({ vendor }) {
  return (
    <div className="flex items-start justify-between gap-4 flex-wrap" data-testid="invoicing-header">
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-violet-300/80 mb-1">Invoicing</div>
        <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight">
          {vendor?.business_name || 'Your business'}
        </h1>
        <p className="text-white/50 mt-2 text-sm">Send polished invoices in minutes.</p>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <Link to="/website-builder/invoicing/invoices/new" data-testid="create-invoice-btn"
          className="px-4 py-2.5 rounded-lg bg-violet-500 hover:bg-violet-400 text-white text-sm font-semibold inline-flex items-center gap-2 shadow-[0_8px_24px_-8px_rgba(139,92,246,0.6)]">
          <Plus className="w-4 h-4" /> Create Invoice
        </Link>
        <Link to="/website-builder/invoicing/clients" data-testid="open-clients-btn"
          className="px-4 py-2.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm font-medium inline-flex items-center gap-2">
          <Users className="w-4 h-4" /> Clients
        </Link>
        <Link to="/website-builder/settings/payments-branding" data-testid="invoicing-settings"
          className="p-2.5 rounded-lg bg-white/5 hover:bg-white/15 text-white/70 border border-white/10">
          <SettingsIcon className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}

/* ───────── Welcome banner ───────── */
function WelcomeBanner({ vendor }) {
  const [dismissed, setDismissed] = useState(() => localStorage.getItem('invoicing-welcome-dismissed') === '1');
  if (dismissed) return null;
  const dismiss = () => { localStorage.setItem('invoicing-welcome-dismissed', '1'); setDismissed(true); };
  return (
    <GlassCard className="relative overflow-hidden p-6 sm:p-8" data-testid="invoicing-welcome">
      <div className="absolute -right-12 -top-12 w-60 h-60 rounded-full bg-gradient-to-br from-violet-500/40 to-fuchsia-500/30 blur-3xl pointer-events-none" />
      <div className="relative flex items-start gap-6 flex-wrap">
        <div className="flex-1 min-w-[260px]">
          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-violet-500/15 border border-violet-400/30 text-violet-200 text-[11px] uppercase tracking-[0.14em] mb-3">
            <Sparkles className="w-3 h-3" /> What&apos;s new
          </div>
          <h2 className="text-2xl font-semibold text-white">Add Client Information & Custom Fields</h2>
          <p className="text-white/60 mt-2 text-sm max-w-xl">
            Look professional with client company names, billing addresses, PO numbers, VAT numbers, project details, and unlimited custom fields.
          </p>
          <div className="flex gap-2 mt-5 flex-wrap">
            <Link to="/website-builder/invoicing/invoices/new" data-testid="welcome-send-invoice"
              className="px-4 py-2 rounded-lg bg-white text-gray-900 text-sm font-semibold hover:bg-white/90 inline-flex items-center gap-2">
              <Receipt className="w-4 h-4" /> Send Invoice
            </Link>
          </div>
        </div>
        <div className="hidden md:block">
          <MockInvoicePreview businessName={vendor?.business_name} />
        </div>
        <button onClick={dismiss} className="absolute top-3 right-3 text-white/40 hover:text-white text-xs">Dismiss</button>
      </div>
    </GlassCard>
  );
}

function MockInvoicePreview({ businessName }) {
  return (
    <div className="w-[280px] rounded-xl bg-gradient-to-br from-white to-gray-50 p-5 shadow-2xl border border-white/20 rotate-1">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-gray-400">Invoice</div>
          <div className="text-xs text-gray-700 font-mono mt-1">INV-2026-0042</div>
        </div>
        <div className="px-2 py-0.5 rounded text-[10px] uppercase font-semibold bg-emerald-500/20 text-emerald-700">Paid</div>
      </div>
      <div className="text-sm font-semibold text-gray-900">{businessName || 'Your Business'}</div>
      <div className="text-[11px] text-gray-500">Billed to: Acme Studio</div>
      <div className="mt-4 space-y-1.5">
        <div className="flex justify-between text-xs"><span className="text-gray-600">Brand consultation</span><span className="text-gray-900">$2,400</span></div>
        <div className="flex justify-between text-xs"><span className="text-gray-600">Website build</span><span className="text-gray-900">$3,600</span></div>
      </div>
      <div className="border-t border-gray-200 mt-3 pt-3 flex justify-between"><span className="text-xs text-gray-500">Total</span><span className="text-sm font-bold text-gray-900">$6,000.00</span></div>
    </div>
  );
}

/* ───────── Revenue cards ───────── */
function RevenueCards({ data, onView }) {
  const r = data || {};
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="revenue-cards">
      <GlassCard className="p-5" data-testid="card-revenue">
        <CardHeader icon={TrendingUp} label="Invoice Revenue" ramp="from-violet-500/30 to-fuchsia-500/20" iconColor="text-violet-300" />
        <div className="text-3xl font-bold text-white mt-3">{fmtMoney(r.total_paid_cents)}</div>
        <div className="text-xs text-white/40 mt-1">Total revenue (all time)</div>
        <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-white/5">
          <Mini label="30d" value={fmtMoney(r.last_30_cents)} />
          <Mini label="90d" value={fmtMoney(r.last_90_cents)} />
          <Mini label="12mo" value={fmtMoney(r.last_365_cents)} />
        </div>
        <button data-testid="view-all-revenue" onClick={() => onView('all')} className="mt-4 text-violet-300 hover:text-violet-200 text-sm font-medium inline-flex items-center gap-1">
          View all <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </GlassCard>

      <GlassCard className="p-5" data-testid="card-paid">
        <CardHeader icon={CheckCircle2} label="Paid" ramp="from-emerald-500/30 to-emerald-500/10" iconColor="text-emerald-300" />
        <div className="text-3xl font-bold text-white mt-3">{r.paid_count || 0}</div>
        <div className="text-xs text-white/40 mt-1">Paid invoices</div>
        <div className="mt-4 pt-4 border-t border-white/5">
          <div className="text-2xl font-semibold text-emerald-300">{fmtMoney(r.paid_total_cents)}</div>
          <div className="text-xs text-white/40 mt-0.5">Total received</div>
        </div>
        <button data-testid="view-paid" onClick={() => onView('paid')} className="mt-4 text-emerald-300 hover:text-emerald-200 text-sm font-medium inline-flex items-center gap-1">
          View paid <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </GlassCard>

      <GlassCard className="p-5" data-testid="card-outstanding">
        <CardHeader icon={AlertCircle} label="Outstanding" ramp="from-amber-500/30 to-orange-500/10" iconColor="text-amber-300" />
        <div className="text-3xl font-bold text-white mt-3">{r.outstanding_count || 0}</div>
        <div className="text-xs text-white/40 mt-1">Open invoices</div>
        <div className="mt-4 pt-4 border-t border-white/5">
          <div className="text-2xl font-semibold text-amber-300">{fmtMoney(r.outstanding_total_cents)}</div>
          <div className="text-xs text-white/40 mt-0.5">Outstanding balance</div>
        </div>
        <button data-testid="view-outstanding" onClick={() => onView('outstanding')} className="mt-4 text-amber-300 hover:text-amber-200 text-sm font-medium inline-flex items-center gap-1">
          View outstanding <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </GlassCard>
    </div>
  );
}

const CardHeader = ({ icon: Icon, label, ramp, iconColor }) => (
  <div className="flex items-center gap-3">
    <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${ramp} border border-white/10 flex items-center justify-center`}>
      <Icon className={`w-5 h-5 ${iconColor}`} />
    </div>
    <div className="text-xs uppercase tracking-[0.14em] text-white/50">{label}</div>
  </div>
);
const Mini = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider text-white/30">{label}</div>
    <div className="text-sm font-semibold text-white/80 mt-0.5">{value}</div>
  </div>
);

/* ───────── Setup checklist ───────── */
function SetupChecklist({ setup }) {
  if (!setup) return null;
  const pct = Math.round((setup.complete || 0) * 100);
  return (
    <GlassCard className="p-6" data-testid="setup-checklist">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white">Get started</h3>
        <div className="text-xs text-white/50">
          <span className="text-violet-300 font-semibold">{setup.done_count}</span> of {setup.total_count} complete · {pct}%
        </div>
      </div>
      <div className="w-full h-1.5 bg-white/5 rounded-full overflow-hidden mb-5">
        <div className="h-full bg-gradient-to-r from-violet-500 to-fuchsia-500 transition-all" style={{ width: `${pct}%` }} />
      </div>
      <div className="space-y-1">
        {setup.items.map(item => (
          <Link key={item.id} to={item.action_url} data-testid={`setup-item-${item.id}`}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/5 transition-colors group">
            {item.done
              ? <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
              : <Circle className="w-5 h-5 text-white/25 group-hover:text-white/50 flex-shrink-0" />}
            <span className={`flex-1 text-sm ${item.done ? 'text-white/40 line-through' : 'text-white/90'}`}>{item.label}</span>
            <ChevronRight className="w-4 h-4 text-white/20 group-hover:text-white/60" />
          </Link>
        ))}
      </div>
    </GlassCard>
  );
}

/* ───────── Activity feed ───────── */
function ActivityFeed({ activity }) {
  const rows = activity?.invoices || [];
  return (
    <GlassCard className="p-6" data-testid="activity-feed">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white">Recent invoices</h3>
        <Link to="/website-builder/invoicing/invoices" className="text-xs text-violet-300 hover:text-violet-200 inline-flex items-center gap-1">
          View all <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
      {rows.length === 0 ? (
        <div className="text-center py-10 text-white/40 text-sm">
          No invoices yet — create your first one above.
        </div>
      ) : (
        <div className="rounded-lg border border-white/5 overflow-hidden">
          <div className="grid grid-cols-12 px-4 py-2.5 bg-white/[0.03] text-[10px] uppercase tracking-wider text-white/40">
            <div className="col-span-3">Invoice #</div>
            <div className="col-span-3">Customer</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2 text-right">Amount</div>
            <div className="col-span-2 text-right">Date</div>
          </div>
          {rows.map(r => (
            <div key={r.id} className="grid grid-cols-12 px-4 py-3 border-t border-white/5 items-center text-sm hover:bg-white/[0.02]" data-testid={`activity-row-${r.id}`}>
              <div className="col-span-3 text-white font-mono text-xs">{r.number}</div>
              <div className="col-span-3 text-white/80 truncate">{r.customer_name}</div>
              <div className="col-span-2">
                <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-semibold ${STATUS_COLORS[r.status] || STATUS_COLORS.draft}`}>{r.status}</span>
              </div>
              <div className="col-span-2 text-right text-white font-medium">${(r.total || 0).toFixed(2)}</div>
              <div className="col-span-2 text-right text-white/50 text-xs">{fmtDate(r.created_at)}</div>
            </div>
          ))}
        </div>
      )}
    </GlassCard>
  );
}
const TabBtn_unused = null; // kept for API stability; component removed

/* ───────── Right rail ───────── */
const TIPS = [
  { id: 'connect-stripe',     label: 'Connect Stripe to start accepting card and ACH payments.',  to: '/website-builder/settings/payments-branding' },
  { id: 'ach-payments',       label: 'Set up ACH payments to save on processing fees.',           to: '/website-builder/settings/payments-branding' },
  { id: 'recurring-invoices', label: 'Set up recurring invoices for retainer clients.',           to: '/website-builder/invoicing/invoices/new?recurring=1' },
  { id: 'branded-domain',     label: 'Verify your domain to send emails from your own brand.',    to: '/website-builder/settings/payments-branding' },
  { id: 'contact-form',       label: 'View contact form submissions to follow up with prospects.', to: '/website-builder/inbox' },
];
function BusinessTips() {
  const [idx, setIdx] = useState(0);
  useEffect(() => { const t = setInterval(() => setIdx(i => (i + 1) % TIPS.length), 6000); return () => clearInterval(t); }, []);
  const tip = TIPS[idx];
  return (
    <GlassCard className="p-5" data-testid="business-tips">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.14em] text-amber-300 mb-3">
        <Lightbulb className="w-3.5 h-3.5" /> Business tip
      </div>
      <p className="text-sm text-white/90 leading-relaxed min-h-[60px]">{tip.label}</p>
      <Link to={tip.to} data-testid={`tip-link-${tip.id}`} className="mt-4 inline-flex items-center gap-1 text-amber-300 hover:text-amber-200 text-sm font-medium">
        Get started <ArrowRight className="w-3.5 h-3.5" />
      </Link>
      <div className="flex gap-1 mt-4">
        {TIPS.map((_, i) => (
          <button key={i} onClick={() => setIdx(i)} className={`h-1 rounded-full transition-all ${i === idx ? 'w-6 bg-amber-300' : 'w-1.5 bg-white/15'}`} />
        ))}
      </div>
    </GlassCard>
  );
}

const PAY_METHODS = ['Visa', 'Mastercard', 'AMEX', 'Discover', 'Apple Pay', 'Google Pay', 'ACH', 'Klarna', 'Afterpay'];
function PaymentMethodsPanel({ stripeConnected }) {
  return (
    <GlassCard className="p-5" data-testid="payment-methods-panel">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-white inline-flex items-center gap-2"><CreditCard className="w-4 h-4 text-white/60" /> Payment methods</h4>
        {!stripeConnected && (
          <Link to="/website-builder/settings/payments-branding" data-testid="turn-on-payments" className="text-xs text-violet-300 hover:text-violet-200">Turn on →</Link>
        )}
      </div>
      <div className="grid grid-cols-3 gap-1.5">
        {PAY_METHODS.map(m => (
          <div key={m} className={`px-2 py-1.5 rounded text-[11px] text-center border ${stripeConnected ? 'bg-white/5 border-white/10 text-white/80' : 'bg-white/[0.02] border-white/5 text-white/30'}`}>
            {m}
          </div>
        ))}
      </div>
      {!stripeConnected && (
        <Link to="/website-builder/settings/payments-branding" data-testid="enable-payments"
          className="mt-4 w-full px-4 py-2 rounded-lg bg-violet-500 hover:bg-violet-400 text-white text-sm font-semibold inline-flex items-center justify-center gap-2">
          <ExternalLink className="w-4 h-4" /> Turn On Payments
        </Link>
      )}
    </GlassCard>
  );
}

/* ───────── Page ───────── */
export default function InvoicingDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/builder/invoicing/dashboard`, { headers: headers() });
        if (active) setData(data);
      } catch (e) { /* surface inline */ }
      finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, []);

  const handleView = (filter) => navigate(`/website-builder/invoicing/invoices?filter=${filter}`);
  const stripeConnected = !!data?.vendor?.stripe_charges_enabled;

  return (
    <div className="min-h-full px-4 sm:px-6 lg:px-10 py-8 sm:py-10 max-w-7xl mx-auto space-y-8" data-testid="invoicing-dashboard">
      <TopHeader vendor={data?.vendor} />
      <WelcomeBanner vendor={data?.vendor} />
      {loading ? (
        <div className="text-center text-white/40 py-20">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <RevenueCards data={data?.revenue} onView={handleView} />
            <SetupChecklist setup={data?.setup} />
            <ActivityFeed activity={data?.activity} />
          </div>
          <div className="space-y-4">
            <BusinessTips />
            <PaymentMethodsPanel stripeConnected={stripeConnected} />
          </div>
        </div>
      )}
    </div>
  );
}
